// SGNL-156 — Pipeline analytics tabs.
//
// Stage Velocity (SGNL-68 FE):
//   Heatmap of stage transitions. Date range + week/month grouping.
//   Mode toggle (count / $$ volume) is client-side — no refetch on toggle.
//   Cell click drills into Deal Explorer.
//
// Conversion Funnel (SGNL-69 FE):
//   Cohort table + CSV export.

import { fetchConversionFunnel, fetchStageVelocity } from "../api";
import { setFilters } from "../state";
import type { ConversionFunnelResult, FunnelCohort, StageVelocityResult } from "../types";
import { esc, fmtMoney } from "../lib/format";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function renderLoadingShell(title: string): string {
  return `<div class="tab-content" data-testid="analytics-loading">
    <div class="tab-header"><h2>${esc(title)}</h2></div>
    <p class="muted">Loading…</p>
  </div>`;
}

// ARCH-1: AbortController per host so a fast tab-switch cancels the
// in-flight fetch and the stale result doesn't overwrite the new state.
const _velAbort = new WeakMap<HTMLElement, AbortController>();
const _funnelAbort = new WeakMap<HTMLElement, AbortController>();

// SGNL-206 (CR F-01 / PRA-1): cancel exports so pipeline_analytics.ts
// can abort the SIBLING renderer's in-flight fetch when swapping
// sub-views. Without these, switching Velocity → Funnel leaves
// velocity's fetch running; when it resolves it overwrites the
// funnel body with stale content. analytics.ts only aborts its own
// renderer's prior fetch — the sibling map is invisible to it.
export function _abortStageVelocity(host: HTMLElement): void {
  _velAbort.get(host)?.abort();
  _velAbort.delete(host);
}
export function _abortConversionFunnel(host: HTMLElement): void {
  _funnelAbort.get(host)?.abort();
  _funnelAbort.delete(host);
}

// ---------------------------------------------------------------------------
// CSV quoting (RFC-4180)  — SEC-2 / CQ-3
// ---------------------------------------------------------------------------

function _csvField(v: string | number): string {
  const s = String(v);
  // Quote if the value contains comma, double-quote, or newline.
  if (/[",\n\r]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}

export function _funnelToCsv(data: ConversionFunnelResult): string {
  const allStages = [
    ...new Set(data.cohorts.flatMap((c) => c.stages.map((s) => s.stage))),
  ].sort();
  const header = ["cohort_month", ...allStages].map(_csvField).join(",");
  const rows = data.cohorts.map((cohort) => {
    const byStage = new Map(cohort.stages.map((s) => [s.stage, s.opp_count]));
    return [cohort.cohort_month, ...allStages.map((s) => byStage.get(s) ?? 0)]
      .map(_csvField)
      .join(",");
  });
  return [header, ...rows].join("\n");
}

// ---------------------------------------------------------------------------
// Stage Velocity — heatmap
// ---------------------------------------------------------------------------

export function _buildHeatmap(
  data: StageVelocityResult,
  mode: "count" | "amount",
): string {
  const { transitions } = data;
  if (!transitions.length) {
    return `<p class="empty-bucket" data-testid="heatmap-empty">No stage transitions found in the selected period.</p>`;
  }

  const fromStages = [...new Set(transitions.map((t) => t.from_stage))].sort();
  const toStages = [...new Set(transitions.map((t) => t.to_stage))].sort();

  const lookup = new Map<string, { count: number; amount: number }>();
  for (const t of transitions) {
    const key = `${t.from_stage}|||${t.to_stage}`;
    const cur = lookup.get(key) ?? { count: 0, amount: 0 };
    cur.count += t.opp_count;
    cur.amount += t.total_amount ?? 0;
    lookup.set(key, cur);
  }

  const maxVal = Math.max(
    1,
    ...Array.from(lookup.values()).map((v) => (mode === "count" ? v.count : v.amount)),
  );

  const header = `<tr><th></th>${toStages
    .map(
      (s) =>
        `<th class="muted" style="font-size:0.8em; padding:4px 6px; white-space:nowrap;">${esc(s)}</th>`,
    )
    .join("")}</tr>`;

  const rows = fromStages
    .map((from) => {
      const cells = toStages.map((to) => {
        const v = lookup.get(`${from}|||${to}`);
        if (!v)
          return `<td style="background:var(--surface); padding:6px 8px; text-align:center;">—</td>`;
        const val = mode === "count" ? v.count : v.amount;
        const intensity = Math.round((val / maxVal) * 100);
        const label = mode === "count" ? String(v.count) : fmtMoney(v.amount);
        return (
          `<td data-testid="heatmap-cell" data-from="${esc(from)}" data-to="${esc(to)}" ` +
          `style="background:hsl(213,80%,${100 - intensity * 0.5}%); padding:6px 8px; ` +
          `text-align:center; cursor:pointer; font-size:0.9em; white-space:nowrap;" ` +
          `title="${esc(from)} → ${esc(to)}: ${esc(label)}">${esc(label)}</td>`
        );
      });
      return `<tr><td style="font-size:0.85em; padding:4px 6px; white-space:nowrap; font-weight:600;">${esc(from)}</td>${cells.join("")}</tr>`;
    })
    .join("\n");

  return `<div style="overflow-x:auto;">
    <table class="data-table" style="min-width:400px; border-collapse:collapse;">
      <thead>${header}</thead>
      <tbody>${rows}</tbody>
    </table>
  </div>
  <p class="muted" style="margin-top:8px; font-size:0.85em;">
    Click a cell to drill into Deal Explorer.
    total_amount = transition-event $ volume, not deduplicated pipeline value.
  </p>`;
}

function _velocityControls(
  dateFrom: string,
  dateTo: string,
  grouping: "week" | "month",
  mode: "count" | "amount",
): string {
  return `<div style="display:flex; gap:12px; flex-wrap:wrap; align-items:center; margin-bottom:16px;">
    <label style="font-size:0.9em;">From <input type="date" data-action="vel-from" value="${esc(dateFrom)}" style="margin-left:4px; padding:2px 6px;"></label>
    <label style="font-size:0.9em;">To <input type="date" data-action="vel-to" value="${esc(dateTo)}" style="margin-left:4px; padding:2px 6px;"></label>
    <div style="display:flex; gap:6px;">
      <button class="action-link${grouping === "week" ? " active" : ""}" data-action="vel-grouping" data-value="week" type="button">Week</button>
      <button class="action-link${grouping === "month" ? " active" : ""}" data-action="vel-grouping" data-value="month" type="button">Month</button>
    </div>
    <div style="display:flex; gap:6px;">
      <button class="action-link${mode === "count" ? " active" : ""}" data-action="vel-mode" data-value="count" type="button"># Moves</button>
      <button class="action-link${mode === "amount" ? " active" : ""}" data-action="vel-mode" data-value="amount" type="button">$ Volume</button>
    </div>
    <button class="action-link" data-action="vel-refresh" type="button">↺ Refresh</button>
  </div>`;
}

export async function renderStageVelocityTab(host: HTMLElement): Promise<void> {
  // ARCH-1: cancel any prior in-flight fetch for this host.
  _velAbort.get(host)?.abort();
  const controller = new AbortController();
  _velAbort.set(host, controller);

  const today = new Date().toISOString().slice(0, 10);
  const ninetyAgo = new Date(Date.now() - 90 * 86400_000).toISOString().slice(0, 10);

  let dateFrom = ninetyAgo;
  let dateTo = today;
  let grouping: "week" | "month" = "week";
  let mode: "count" | "amount" = "count";
  let lastData: StageVelocityResult | null = null; // ARCH-2: cache for mode toggle

  async function load(): Promise<void> {
    host.innerHTML = renderLoadingShell("Stage Velocity");
    try {
      const data = await fetchStageVelocity({ from: dateFrom, to: dateTo, grouping });
      if (controller.signal.aborted) return; // ARCH-1: stale response, discard
      lastData = data;
      render(data);
    } catch (err) {
      if (controller.signal.aborted) return;
      const msg = err instanceof Error ? err.message : String(err);
      host.innerHTML = `<div class="tab-content"><div class="tab-header"><h2>Stage Velocity</h2></div>
        <p class="muted">Couldn't load (${esc(msg)}).</p>
        <button class="action-link" data-action="vel-refresh" type="button">Retry</button>
      </div>`;
      host
        .querySelector<HTMLButtonElement>('[data-action="vel-refresh"]')
        ?.addEventListener("click", () => void load());
    }
  }

  function render(data: StageVelocityResult): void {
    // ARCH-3 / SEC-1: esc() server-echoed date strings.
    host.innerHTML = `
      <div class="tab-content" data-testid="stage-velocity-tab">
        <div class="tab-header">
          <h2>Stage Velocity <span class="muted" style="font-size:0.7em; font-weight:normal;">${esc(data.date_from)} → ${esc(data.date_to)}</span></h2>
        </div>
        <p class="muted" style="margin:0 0 12px;">How many opps (and $$) moved between stages in the selected window.</p>
        ${_velocityControls(dateFrom, dateTo, grouping, mode)}
        <div data-testid="velocity-heatmap">${_buildHeatmap(data, mode)}</div>
      </div>`;
    bindVelocityControls();
  }

  function bindVelocityControls(): void {
    // CQ-2: date inputs auto-load (consistent with grouping buttons).
    host
      .querySelector<HTMLInputElement>('[data-action="vel-from"]')
      ?.addEventListener("change", (e) => {
        dateFrom = (e.target as HTMLInputElement).value;
        void load();
      });
    host
      .querySelector<HTMLInputElement>('[data-action="vel-to"]')
      ?.addEventListener("change", (e) => {
        dateTo = (e.target as HTMLInputElement).value;
        void load();
      });
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-action="vel-grouping"]')) {
      btn.addEventListener("click", () => {
        grouping = (btn.dataset.value ?? "week") as "week" | "month";
        void load();
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-action="vel-mode"]')) {
      btn.addEventListener("click", () => {
        mode = (btn.dataset.value ?? "count") as "count" | "amount";
        // ARCH-2: mode is client-side — re-render from cache, no refetch.
        if (lastData) render(lastData);
        else void load();
      });
    }
    host
      .querySelector<HTMLButtonElement>('[data-action="vel-refresh"]')
      ?.addEventListener("click", () => void load());

    host.querySelector('[data-testid="velocity-heatmap"]')?.addEventListener("click", (e) => {
      const cell = (e.target as HTMLElement).closest<HTMLElement>("[data-testid='heatmap-cell']");
      if (!cell) return;
      const to = cell.dataset.to ?? "";
      // SGNL-249 — stage filter is multi-select now. A heatmap-cell
      // drill-through carries one stage; set the list to exactly
      // that stage (replacing any prior multi-selection).
      if (to) setFilters({ stages: [to] });
      document.querySelector<HTMLButtonElement>('button[data-tab="deals"]')?.click();
    });
  }

  await load();
}

// ---------------------------------------------------------------------------
// Conversion Funnel
// ---------------------------------------------------------------------------

function _renderFunnelTable(cohorts: FunnelCohort[]): string {
  if (!cohorts.length) {
    return `<p class="empty-bucket" data-testid="funnel-empty">No cohort data found in the selected period.</p>`;
  }

  const allStages = [
    ...new Set(cohorts.flatMap((c) => c.stages.map((s) => s.stage))),
  ].sort();
  const colors = [
    "#4A90D9","#7B61FF","#00C897","#F5A623","#E74C3C",
    "#1ABC9C","#9B59B6","#E67E22","#2ECC71","#3498DB",
  ];

  const headerCells = allStages
    .map((s) => `<th style="font-size:0.8em; padding:4px 8px;">${esc(s)}</th>`)
    .join("");

  const rows = cohorts.map((cohort) => {
    const byStage = new Map(cohort.stages.map((s) => [s.stage, s]));
    const cells = allStages.map((stage, i) => {
      const s = byStage.get(stage);
      if (!s)
        return `<td style="padding:4px 8px; text-align:center; color:var(--slate);">—</td>`;
      const color = colors[i % colors.length];
      return (
        `<td style="padding:4px 8px; text-align:center;" title="${esc(stage)}: ${s.opp_count} opps">` +
        `<span style="display:inline-block; min-width:28px; padding:2px 6px; border-radius:3px; ` +
        `background:${color}20; color:${color}; font-size:0.9em; font-weight:600;">${s.opp_count}</span>` +
        `</td>`
      );
    });
    return `<tr><td style="font-weight:600; padding:4px 8px; white-space:nowrap;">${esc(cohort.cohort_month)}</td>${cells.join("")}</tr>`;
  });

  return `<div style="overflow-x:auto;" data-testid="funnel-table">
    <table class="data-table" style="border-collapse:collapse;">
      <thead><tr><th style="padding:4px 8px;">Cohort</th>${headerCells}</tr></thead>
      <tbody>${rows.join("\n")}</tbody>
    </table>
  </div>
  <p class="muted" style="margin-top:8px; font-size:0.85em;">
    Each row = opps that entered the pipeline in that calendar month.
  </p>`;
}

export async function renderConversionFunnelTab(host: HTMLElement): Promise<void> {
  // ARCH-1: cancel prior fetch.
  _funnelAbort.get(host)?.abort();
  const controller = new AbortController();
  _funnelAbort.set(host, controller);

  const today = new Date();
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const twelveAgo = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  const fromMonth = `${twelveAgo.getFullYear()}-${String(twelveAgo.getMonth() + 1).padStart(2, "0")}-01`;

  let cohortFrom = fromMonth;
  let cohortTo = thisMonth;

  async function load(): Promise<void> {
    host.innerHTML = renderLoadingShell("Conversion Funnel");
    try {
      const data = await fetchConversionFunnel({
        cohort_month_from: cohortFrom,
        cohort_month_to: cohortTo,
      });
      if (controller.signal.aborted) return;
      render(data);
    } catch (err) {
      if (controller.signal.aborted) return;
      const msg = err instanceof Error ? err.message : String(err);
      // CQ-4: error path doesn't call bindControls; binds Refresh inline.
      host.innerHTML = `<div class="tab-content"><div class="tab-header"><h2>Conversion Funnel</h2></div>
        <p class="muted">Couldn't load (${esc(msg)}).</p>
        <button class="action-link" data-action="funnel-refresh" type="button">Retry</button>
      </div>`;
      host
        .querySelector<HTMLButtonElement>('[data-action="funnel-refresh"]')
        ?.addEventListener("click", () => void load());
    }
  }

  function render(data: ConversionFunnelResult): void {
    host.innerHTML = `
      <div class="tab-content" data-testid="conversion-funnel-tab">
        <div class="tab-header">
          <h2>Conversion Funnel</h2>
          <div style="display:flex; gap:8px; align-items:center;">
            <button class="btn-secondary" data-action="funnel-csv" type="button">Export CSV</button>
            <button class="action-link" data-action="funnel-refresh" type="button">↺ Refresh</button>
          </div>
        </div>
        <p class="muted" style="margin:0 0 12px;">Opps that entered the pipeline each month and what stages they reached.</p>
        <div style="display:flex; gap:12px; flex-wrap:wrap; align-items:center; margin-bottom:16px;">
          <label style="font-size:0.9em;">From <input type="month" data-action="funnel-from" value="${esc(cohortFrom.slice(0, 7))}" style="margin-left:4px; padding:2px 6px;"></label>
          <label style="font-size:0.9em;">To <input type="month" data-action="funnel-to" value="${esc(cohortTo.slice(0, 7))}" style="margin-left:4px; padding:2px 6px;"></label>
        </div>
        <div data-testid="funnel-content">${_renderFunnelTable(data.cohorts)}</div>
      </div>`;
    bindFunnelControls(data);
  }

  function bindFunnelControls(data: ConversionFunnelResult): void {
    // CQ-2: month inputs auto-load.
    host
      .querySelector<HTMLInputElement>('[data-action="funnel-from"]')
      ?.addEventListener("change", (e) => {
        const v = (e.target as HTMLInputElement).value;
        if (v) { cohortFrom = `${v}-01`; void load(); }
      });
    host
      .querySelector<HTMLInputElement>('[data-action="funnel-to"]')
      ?.addEventListener("change", (e) => {
        const v = (e.target as HTMLInputElement).value;
        if (v) { cohortTo = `${v}-01`; void load(); }
      });
    host
      .querySelector<HTMLButtonElement>('[data-action="funnel-refresh"]')
      ?.addEventListener("click", () => void load());
    host
      .querySelector<HTMLButtonElement>('[data-action="funnel-csv"]')
      ?.addEventListener("click", () => {
        const csv = _funnelToCsv(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `signal-funnel-${data.cohort_month_from}-to-${data.cohort_month_to}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  }

  await load();
}
